import { syncUser } from "../services/api/app/auth";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isRetriableAuthError(error) {
  const status = error?.response?.status;
  if (!status) return true; // network / timeout
  if (status === 401 || status === 403 || status === 503) return true;
  const message = String(error?.message || error?.response?.data?.detail || "");
  return /not synced|sign-in|token|forbidden|unauthorized/i.test(message);
}

/**
 * Run an authenticated API call only after Clerk token + backend sync are ready.
 * Retries transient auth/sync failures so manage pages don't flash empty on cold load.
 *
 * @template T
 * @param {() => Promise<string|null|undefined>} getToken
 * @param {(token: string) => Promise<T>} requestFn
 * @param {{ maxAttempts?: number }} [options]
 * @returns {Promise<T>}
 */
export async function withAuthReadyRequest(getToken, requestFn, options = {}) {
  const maxAttempts = options.maxAttempts ?? 8;
  let lastError = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const token = await getToken();
    if (!token) {
      lastError = new Error("Sign in required");
      await sleep(500 + attempt * 150);
      continue;
    }

    try {
      await syncUser(token);
    } catch (syncErr) {
      // User may already exist; still attempt the request, then retry if it fails.
      lastError = syncErr;
    }

    try {
      return await requestFn(token);
    } catch (error) {
      lastError = error;
      if (isRetriableAuthError(error) && attempt < maxAttempts - 1) {
        await sleep(700 + attempt * 250);
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error("Authentication not ready");
}

export default withAuthReadyRequest;
